import { useState, useMemo } from 'react';
import { Seat } from './types';
import { MAX_SELECTED_ROOMS, isSelectable, isRoomSeat } from './rules';

export function useSeatSelection(seats: Seat[]) {
  const [selectedSeatIds, setSelectedSeatIds] = useState<Set<string>>(new Set());

  // Create a lookup map for seats by ID for O(1) access
  const seatMap = useMemo(() => {
    const map = new Map<string, Seat>();
    seats.forEach((seat) => map.set(seat.id, seat));
    return map;
  }, [seats]);

  const toggleSeat = (seatId: string) => {
    const seat = seatMap.get(seatId);
    if (!seat || !isSelectable(seat)) {
      return;
    }

    setSelectedSeatIds((prev) => {
      const next = new Set(prev);
      
      if (prev.has(seatId)) {
        // Deselect - remove only this seat
        next.delete(seatId);
      } else {
        // Select new seat - add to existing selections
        next.add(seatId);
      }
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedSeatIds(new Set());
  };

  const isSeatSelected = (seatId: string) => {
    return selectedSeatIds.has(seatId);
  };

  // Check if we can select more room seats
  const selectedRoomSeats = useMemo(() => {
    return seats.filter((seat) => 
      selectedSeatIds.has(seat.id) && isRoomSeat(seat)
    );
  }, [seats, selectedSeatIds]);

  const canSelectMoreRooms = selectedRoomSeats.length < MAX_SELECTED_ROOMS;
  const canSelectMore = true; // Open seats have no limit, so always true

  const selectedSeats = useMemo(() => {
    return seats.filter((seat) => selectedSeatIds.has(seat.id));
  }, [seats, selectedSeatIds]);

  // Find sections where all selectable seats are selected
  const fullySelectedSectionIds = useMemo(() => {
    const sectionMap = new Map<string, { total: number; selected: number }>();
    
    seats.forEach((seat) => {
      if (isSelectable(seat)) {
        const current = sectionMap.get(seat.sectionId) || { total: 0, selected: 0 };
        current.total++;
        if (selectedSeatIds.has(seat.id)) {
          current.selected++;
        }
        sectionMap.set(seat.sectionId, current);
      }
    });
    
    const fullySelected: string[] = [];
    sectionMap.forEach((counts, sectionId) => {
      if (counts.total > 0 && counts.selected === counts.total) {
        fullySelected.push(sectionId);
      }
    });
    
    return new Set(fullySelected);
  }, [seats, selectedSeatIds]);

  return {
    selectedSeatIds,
    selectedSeats,
    toggleSeat,
    clearSelection,
    isSeatSelected,
    canSelectMore,
    canSelectMoreRooms,
    selectedRoomSeats,
    fullySelectedSectionIds,
  };
}

